import { FC, useCallback, useMemo, useState } from 'react';
// mui components
import { Avatar, AvatarGroup, Grid } from '@mui/material';
// modals
import UsersModal from './components/users-modal';
// custom hooks
import { useGetAdaptiveProps } from 'hooks/useGetAdaptiveProps';
import { useSidePanel } from 'hooks/useSidePanel';
// api
import { IProject, IProjectProfiles, IProjectsProfiles } from 'redux-store/api/projects';
// utils
import { getImageBySize } from 'utils/helpers';
//styles
import { accountsButtonStyles } from '../styles/styles';

interface IAccountsButton {
  project: IProject;
}

const AccountsButton: FC<IAccountsButton> = ({ project }) => {
  const { setProjectId } = useSidePanel();
  const { usersCount } = useGetAdaptiveProps();

  const [selectedUserProfiles, setSelectedUserProfiles] = useState<IProjectProfiles[] | null>(null);

  const sortedProfiles = useMemo(() => {
    const profiles: IProjectProfiles[] = [];

    if (project) {
      project.profiles?.forEach((profile) => {
        if (profile.role === 'OWNER') {
          profiles.unshift(profile);
        } else {
          profiles.push(profile);
        }
      });
    }

    return profiles;
  }, [project]);

  const projectProfiles: IProjectsProfiles[] = useMemo(
    () => [...(project?.projectsProfiles ?? [])],
    [project]
  );

  const handleCloseAvatarModal = useCallback(() => {
    setSelectedUserProfiles(null);
  }, []);

  const handleUserClick = useCallback(
    (user: IProjectProfiles) => {
      setProjectId(project.id);
      setSelectedUserProfiles([user]);
    },
    [project.id, setProjectId]
  );

  const handleAllUsersClick = useCallback(() => {
    setProjectId(project.id);
    setSelectedUserProfiles(project.profiles);
  }, [project.id, project.profiles, setProjectId]);

  if (!project) {
    return null;
  }

  return (
    <Grid
      item
      sx={{
        ...accountsButtonStyles.accounts,
        marginRight: window.location.pathname.includes('account-dashboard') ? '8px' : '0',
      }}
    >
      <AvatarGroup max={6} spacing={10} sx={accountsButtonStyles.accountsContainer}>
        {sortedProfiles.slice(0, usersCount).map((user) => {
          if (user.avatar) {
            return (
              <Avatar
                key={user.userId}
                alt="avatar"
                src={getImageBySize(user.avatar, 100, 100)}
                sx={accountsButtonStyles.avatar}
                onClick={() => handleUserClick(user)}
              />
            );
          } else {
            const abbreviation = `${user.firstName[0]}${user.lastName[0]}`;

            return (
              <Avatar
                key={user.userId}
                alt="avatar abbreviation"
                sx={accountsButtonStyles.avatarAbbreviation}
                onClick={() => handleUserClick(user)}
              >
                {abbreviation}
              </Avatar>
            );
          }
        })}
      </AvatarGroup>
      {project?.profiles?.length > usersCount && (
        <Avatar sx={accountsButtonStyles.otherAvatarsCount} onClick={handleAllUsersClick}>
          {`+${project.profiles.length - usersCount}`}
        </Avatar>
      )}

      {!!selectedUserProfiles ? (
        <UsersModal
          profiles={selectedUserProfiles}
          projectProfiles={projectProfiles}
          open={!!selectedUserProfiles}
          closeModal={handleCloseAvatarModal}
          type={
            selectedUserProfiles?.length && selectedUserProfiles.length > 1
              ? 'multiple users'
              : 'single user'
          }
          projectId={project.id}
        />
      ) : null}
    </Grid>
  );
};

export default AccountsButton;
